// src/components/FarmPulseContribute.js

/**
 * Two modes: Submit a crop price, or share planting intentions.
 * Every authenticated user can submit. Submissions are always saved.
 * Data becomes "validated" when VALIDATION_THRESHOLD (5) independent
 * contributors report similar prices within a 7-day window.
 */

import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { ChevronDown, Loader2 } from "lucide-react";
import useAuth from "../hooks/useAuth";
import { useToast } from "../contexts/ToastContext";
import {
  submitPrice,
  submitPlantingIntention,
  hasSubmittedToday,
  getUserContributions,
} from "../services/farmPulseService";
import { AGRICULTURAL_CATEGORIES } from "../utils/agriculturalCategories";
import {
  PRICE_UNITS,
  VALIDATION_THRESHOLD,
  RWANDA_TOP_CROPS,
  RWANDA_DISTRICTS,
} from "../constants/farmPulseConstants";
import { errorDev } from "../utils/logger";

// ─── Shared form elements ───────────────────────────────────────
function Select({ value, onChange, disabled, children, className = "" }) {
  return (
    <div className="relative">
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        className={`w-full appearance-none px-3 py-2.5 rounded-lg border border-gray-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-green-500/40 focus:border-green-500 transition pr-9 disabled:opacity-50 disabled:cursor-not-allowed ${className}`}
      >
        {children}
      </select>
      <ChevronDown className="w-3.5 h-3.5 text-gray-400 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
    </div>
  );
}

function Input({ type = "text", className = "", ...props }) {
  return (
    <input
      type={type}
      className={`w-full px-3 py-2.5 rounded-lg border border-gray-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-green-500/40 focus:border-green-500 transition placeholder-gray-400 ${className}`}
      {...props}
    />
  );
}

function FieldLabel({ children }) {
  return (
    <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
      {children}
    </label>
  );
}

// ─── Category / Crop ────────────────────────────────────────────
function CategorySelect({ value, onChange, t }) {
  return (
    <div>
      <FieldLabel>{t("contribute.selectCategory")}</FieldLabel>
      <Select value={value} onChange={onChange}>
        <option value="">{t("contribute.selectCategory")}</option>
        {AGRICULTURAL_CATEGORIES.map((cat) => (
          <option key={cat.id} value={cat.id}>
            {t(`categories.${cat.id}`, { defaultValue: cat.name })}
          </option>
        ))}
      </Select>
    </div>
  );
}

function CropSelect({ categoryId, value, onChange, t }) {
  const category = AGRICULTURAL_CATEGORIES.find((c) => c.id === categoryId);
  const options = category?.examples || [];
  const sorted = useMemo(() => {
    return [...options].sort((a, b) => {
      const aTop = RWANDA_TOP_CROPS.includes(a.toLowerCase().replace(/\s+/g, "_"));
      const bTop = RWANDA_TOP_CROPS.includes(b.toLowerCase().replace(/\s+/g, "_"));
      if (aTop && !bTop) return -1;
      if (!aTop && bTop) return 1;
      return a.localeCompare(b);
    });
  }, [options]);
  return (
    <div>
      <FieldLabel>{t("contribute.selectCrop")}</FieldLabel>
      <Select value={value} onChange={onChange} disabled={!categoryId}>
        <option value="">{t("contribute.selectCrop")}</option>
        {sorted.map((crop) => (
          <option key={crop} value={crop}>
            {t(`crops.${crop.toLowerCase().replace(/\s+/g, "_")}`, { defaultValue: crop })}
          </option>
        ))}
      </Select>
    </div>
  );
}

function DistrictSelect({ value, onChange, t }) {
  return (
    <div>
      <FieldLabel>{t("contribute.district", { defaultValue: "District" })}</FieldLabel>
      <Select value={value} onChange={onChange}>
        <option value="">{t("contribute.selectDistrict", { defaultValue: "Select district" })}</option>
        {RWANDA_DISTRICTS.map((d) => (
          <option key={d} value={d}>{d}</option>
        ))}
      </Select>
    </div>
  );
}

// ─── Stats row (text-only, no decorative icons) ─────────────────
function ContributionStats({ stats }) {
  if (!stats) return null;
  return (
    <div className="grid grid-cols-3 gap-px bg-gray-100 dark:bg-neutral-800 rounded-lg overflow-hidden text-center">
      {[
        { value: stats.totalSubmissions, label: "Total" },
        { value: stats.validatedPrices, label: "Validated" },
        { value: stats.plantingReports, label: "Planting" },
      ].map(({ value, label }) => (
        <div key={label} className="bg-white dark:bg-neutral-900 px-3 py-2.5">
          <div className="text-base font-bold text-gray-900 dark:text-white">{value}</div>
          <div className="text-[10px] text-gray-400 uppercase tracking-wider">{label}</div>
        </div>
      ))}
    </div>
  );
}

// ─── Price Form ─────────────────────────────────────────────────
function PriceForm({ t, user, onSuccess, stats }) {
  const [category, setCategory] = useState("");
  const [crop, setCrop] = useState("");
  const [price, setPrice] = useState("");
  const [unit, setUnit] = useState("kg");
  const [district, setDistrict] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const toast = useToast();

  useEffect(() => {
    if (!success) return;
    const timer = setTimeout(() => setSuccess(false), 4000);
    return () => clearTimeout(timer);
  }, [success]);

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      if (!crop || !price || !user) return;
      setError("");
      setSubmitting(true);
      try {
        let alreadyDone = false;
        try { alreadyDone = await hasSubmittedToday(user.uid, crop); } catch (err) { errorDev("Operation failed:", err);  /* index may be missing */ }
        if (alreadyDone) { setError(t("contribute.alreadySubmitted")); setSubmitting(false); return; }
        await submitPrice({
          userId: user.uid,
          userName: user.displayName || user.email?.split("@")[0] || "Farmer",
          category, crop, price: parseFloat(price), unit, district,
        });
        setSuccess(true);
        toast.success(t("contribute.successPrice"));
        setPrice(""); setCrop(""); setCategory(""); setDistrict("");
        if (onSuccess) onSuccess();
      } catch (err) {
        errorDev("Price submission error:", err);
        setError(t("contribute.errorSubmit"));
      } finally { setSubmitting(false); }
    },
    [crop, price, unit, category, district, user, t, toast, onSuccess]
  );

  return (
    <form onSubmit={handleSubmit} className="space-y-3">
      {/* Validation notice */}
      <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed">
        {t("contribute.priceDescription", { threshold: VALIDATION_THRESHOLD })}
      </p>

      <ContributionStats stats={stats} />

      <CategorySelect value={category} onChange={(v) => { setCategory(v); setCrop(""); }} t={t} />
      <CropSelect categoryId={category} value={crop} onChange={setCrop} t={t} />

      <div className="grid grid-cols-2 gap-2">
        <div>
          <FieldLabel>{t("contribute.enterPrice")}</FieldLabel>
          <Input type="number" inputMode="numeric" min="1" step="any" value={price} onChange={(e) => setPrice(e.target.value)} placeholder="0" required />
        </div>
        <div>
          <FieldLabel>{t("contribute.unit", { defaultValue: "Unit" })}</FieldLabel>
          <Select value={unit} onChange={setUnit}>
            {PRICE_UNITS.map((u) => (
              <option key={u} value={u}>{t("contribute.perUnit")} {t(`units.${u}`)}</option>
            ))}
          </Select>
        </div>
      </div>

      <DistrictSelect value={district} onChange={setDistrict} t={t} />

      {error && (
        <p className="text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/10 rounded-lg px-3 py-2">{error}</p>
      )}
      {success && (
        <p className="text-sm text-green-700 dark:text-green-300 bg-green-50 dark:bg-green-900/10 rounded-lg px-3 py-2">{t("contribute.successPrice")}</p>
      )}

      <button
        type="submit"
        disabled={!crop || !price || submitting || !user}
        className="w-full px-4 py-2.5 rounded-lg bg-green-700 hover:bg-green-800 active:bg-green-900 disabled:bg-gray-300 dark:disabled:bg-neutral-700 text-white font-semibold text-sm transition-all disabled:cursor-not-allowed"
      >
        {submitting ? (
          <span className="flex items-center justify-center gap-2">
            <span className="orrith-loader orrith-loader-xs orrith-loader-on-dark" />
            {t("contribute.submitting")}
          </span>
        ) : t("contribute.submitPrice")}
      </button>
    </form>
  );
}

// ─── Planting Form ──────────────────────────────────────────────
function PlantingForm({ t, user, onSuccess }) {
  const [category, setCategory] = useState("");
  const [crop, setCrop] = useState("");
  const [status, setStatus] = useState("planted");
  const [plantDate, setPlantDate] = useState("");
  const [area, setArea] = useState("");
  const [district, setDistrict] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const toast = useToast();

  useEffect(() => {
    if (!success) return;
    const timer = setTimeout(() => setSuccess(false), 4000);
    return () => clearTimeout(timer);
  }, [success]);

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      if (!crop || !user) return;
      setError("");
      setSubmitting(true);
      try {
        await submitPlantingIntention({
          userId: user.uid,
          userName: user.displayName || user.email?.split("@")[0] || "Farmer",
          category, crop, status, plantDate: plantDate || null,
          areaHectares: area ? parseFloat(area) : 0, district,
        });
        setSuccess(true);
        toast.success(t("contribute.successPlanting"));
        setCrop(""); setCategory(""); setPlantDate(""); setArea(""); setDistrict("");
        if (onSuccess) onSuccess();
      } catch (err) {
        errorDev("Planting submission error:", err);
        setError(t("contribute.errorSubmit"));
      } finally { setSubmitting(false); }
    },
    [crop, category, status, plantDate, area, district, user, t, toast, onSuccess]
  );

  return (
    <form onSubmit={handleSubmit} className="space-y-3">
      <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed">
        {t("contribute.plantingDescription")}
      </p>

      <CategorySelect value={category} onChange={(v) => { setCategory(v); setCrop(""); }} t={t} />
      <CropSelect categoryId={category} value={crop} onChange={setCrop} t={t} />

      {/* Status toggle — text only */}
      <div>
        <FieldLabel>{t("contribute.planted", { defaultValue: "Status" })}</FieldLabel>
        <div className="grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={() => setStatus("planted")}
            className={`px-3 py-2 rounded-lg text-sm font-medium border transition-all ${
              status === "planted"
                ? "bg-green-50 dark:bg-green-900/20 border-green-300 dark:border-green-700 text-green-700 dark:text-green-300"
                : "border-gray-200 dark:border-neutral-700 text-gray-500 dark:text-gray-400 hover:border-gray-300"
            }`}
          >
            {t("contribute.planted")}
          </button>
          <button
            type="button"
            onClick={() => setStatus("plan_to_plant")}
            className={`px-3 py-2 rounded-lg text-sm font-medium border transition-all ${
              status === "plan_to_plant"
                ? "bg-amber-50 dark:bg-amber-900/20 border-amber-300 dark:border-amber-700 text-amber-700 dark:text-amber-300"
                : "border-gray-200 dark:border-neutral-700 text-gray-500 dark:text-gray-400 hover:border-gray-300"
            }`}
          >
            {t("contribute.planToPlant")}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <div>
          <FieldLabel>{status === "planted" ? t("contribute.whenPlanted") : t("contribute.whenPlan")}</FieldLabel>
          <Input type="date" value={plantDate} onChange={(e) => setPlantDate(e.target.value)} />
        </div>
        <div>
          <FieldLabel>{t("contribute.areaSize")}</FieldLabel>
          <Input type="number" inputMode="decimal" min="0" step="0.01" value={area} onChange={(e) => setArea(e.target.value)} placeholder="0.5" />
        </div>
      </div>

      <DistrictSelect value={district} onChange={setDistrict} t={t} />

      {error && (
        <p className="text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/10 rounded-lg px-3 py-2">{error}</p>
      )}
      {success && (
        <p className="text-sm text-green-700 dark:text-green-300 bg-green-50 dark:bg-green-900/10 rounded-lg px-3 py-2">{t("contribute.successPlanting")}</p>
      )}

      <button
        type="submit"
        disabled={!crop || submitting || !user}
        className="w-full px-4 py-2.5 rounded-lg bg-green-700 hover:bg-green-800 active:bg-green-900 disabled:bg-gray-300 dark:disabled:bg-neutral-700 text-white font-semibold text-sm transition-all disabled:cursor-not-allowed"
      >
        {submitting ? (
          <span className="flex items-center justify-center gap-2">
            <span className="orrith-loader orrith-loader-xs orrith-loader-on-dark" />
            {t("contribute.submitting")}
          </span>
        ) : t("contribute.submitPlanting")}
      </button>
    </form>
  );
}

// ─── Main Contribute Component ──────────────────────────────────
export default function FarmPulseContribute({ onSuccess }) {
  const { t } = useTranslation("marketAnalysis");
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState("price");
  const [stats, setStats] = useState(null);

  useEffect(() => {
    if (!user?.uid) return;
    let cancelled = false;
    getUserContributions(user.uid).then((s) => { if (!cancelled) setStats(s); }).catch(() => {});
    return () => { cancelled = true; };
  }, [user?.uid]);

  const handleSuccess = useCallback(() => {
    if (user?.uid) getUserContributions(user.uid).then(setStats).catch(() => {});
    if (onSuccess) onSuccess();
  }, [user?.uid, onSuccess]);

  return (
    <div className="bg-white dark:bg-neutral-900 rounded-xl shadow-sm border border-gray-200 dark:border-neutral-800 overflow-hidden">
      <div className="h-0.5 bg-gradient-to-r from-green-600 to-green-400" />

      <div className="p-4 sm:p-5">
        {/* Header — text only */}
        <div className="mb-3">
          <h2 className="text-sm font-bold text-gray-900 dark:text-white">
            {t("contribute.title")}
          </h2>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
            {t("contribute.subtitle")}
          </p>
        </div>

        {/* Login prompt */}
        {!user && (
          <p className="text-sm text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/15 rounded-lg px-3 py-2.5 mb-3">
            {t("contribute.loginRequired", { defaultValue: "Sign in to contribute market data." })}
          </p>
        )}

        {/* Tab switcher — text-only pills, no icons */}
        <div className="flex gap-px bg-gray-100 dark:bg-neutral-800 rounded-lg p-0.5 mb-4">
          {[
            { key: "price", label: t("contribute.priceTab") },
            { key: "planting", label: t("contribute.plantingTab") },
          ].map(({ key, label }) => (
            <button
              key={key}
              type="button"
              onClick={() => setActiveTab(key)}
              className={`flex-1 px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
                activeTab === key
                  ? "bg-green-700 text-white shadow-sm"
                  : "text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {activeTab === "price" ? (
          <PriceForm t={t} user={user} onSuccess={handleSuccess} stats={stats} />
        ) : (
          <PlantingForm t={t} user={user} onSuccess={handleSuccess} />
        )}
      </div>
    </div>
  );
}
